import { useQuery, skipToken } from '@tanstack/react-query';
import { isPermissionRefused, type UserDevice } from '@citibox-panel/api-client';

export interface UseUserDevice {
  device: UserDevice | null | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

// Device telemetry, keyed by the numeric USER ID. The query is skipped (skipToken)
// until the account resolves the id, which also narrows it to a string inside the
// query function. The error is left as inline state (default throwOnError: false):
// a device failure must surface a retry on the device card alone, never tear down
// the account card via the shell boundary.
export function useUserDevice(
  id: string | undefined,
  fetchDevice: (id: string) => Promise<UserDevice | null>,
): UseUserDevice {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['user-device', id],
    queryFn: id === undefined ? skipToken : () => fetchDevice(id),
  });

  // A permission refusal is the host's to show: it replaces the whole view, so reporting it here
  // as a failed read would only paint an inline failure under the access screen.
  return {
    device: data,
    isPending,
    isError: isError && !isPermissionRefused(error),
    refetch: () => void refetch(),
  };
}
